// Mirrors Wheels.ERP.Shared.Accounting DTOs.

export const ACCOUNT_TYPE = {
  ASSET: 1,
  LIABILITY: 2,
  EQUITY: 3,
  REVENUE: 4,
  EXPENSE: 5,
} as const;

export type AccountType = (typeof ACCOUNT_TYPE)[keyof typeof ACCOUNT_TYPE];

export const NORMAL_BALANCE = {
  DEBIT: 1,
  CREDIT: 2,
} as const;

export type NormalBalance = (typeof NORMAL_BALANCE)[keyof typeof NORMAL_BALANCE];

export interface AccountDto {
  accountId: string;
  code: string;
  nameAr: string;
  nameEn: string | null;
  parentAccountId: string | null;
  accountType: AccountType;
  normalBalance: NormalBalance;
  isPostable: boolean;
  isSystem: boolean;
  currencyCode: string;
  isActive: boolean;
}

export interface CreateAccountRequest {
  code: string;
  nameAr: string;
  nameEn: string | null;
  parentAccountId: string;
  currencyCode: string | null;
}

export interface UpdateAccountRequest {
  code: string;
  nameAr: string;
  nameEn: string | null;
  isActive: boolean;
}

export const JOURNAL_SOURCE = {
  MANUAL: 1,
  POS_SALE: 2,
  PURCHASE_INVOICE: 3,
  CHEQUE: 4,
  EXPENSE: 5,
  ADJUSTMENT: 6,
  GOODS_RECEIPT: 7,
} as const;

export type JournalSource = (typeof JOURNAL_SOURCE)[keyof typeof JOURNAL_SOURCE];

export interface JournalEntryLineDto {
  journalEntryLineId: string;
  accountId: string;
  accountCode: string;
  accountNameAr: string;
  accountNameEn: string | null;
  debit: number;
  credit: number;
  lineOrder: number;
  memo: string | null;
  partyType: number | null;
  partyId: string | null;
}

export interface JournalEntryDto {
  journalEntryId: string;
  entryNumber: string;
  entryDate: string;
  postedAt: string;
  source: JournalSource;
  sourceRefId: string | null;
  branchId: string | null;
  description: string | null;
  isReversal: boolean;
  isOpening: boolean;
  totalDebit: number;
  totalCredit: number;
  lines: JournalEntryLineDto[] | null;
}

export interface JournalPageDto {
  total: number;
  rows: JournalEntryDto[];
}

export interface ManualJournalLineRequest {
  accountId: string;
  debit: number;
  credit: number;
  memo: string | null;
}

export interface CreateManualJournalEntryRequest {
  entryDate: string;
  description: string | null;
  lines: ManualJournalLineRequest[];
}

export interface AccountNode extends AccountDto {
  children: AccountNode[];
  depth: number;
}

/** Build a tree from a flat list, sorted by Code at every level. */
export function buildAccountTree(rows: AccountDto[]): AccountNode[] {
  const byId = new Map<string, AccountNode>();
  for (const r of rows) {
    byId.set(r.accountId, { ...r, children: [], depth: 0 });
  }
  const roots: AccountNode[] = [];
  for (const node of byId.values()) {
    if (node.parentAccountId && byId.has(node.parentAccountId)) {
      const parent = byId.get(node.parentAccountId)!;
      node.depth = parent.depth + 1;
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }
  const sortRecursive = (nodes: AccountNode[]) => {
    nodes.sort((a, b) => a.code.localeCompare(b.code));
    nodes.forEach((n) => {
      // Children depths get fixed during walk because Maps don't
      // guarantee insertion order matches tree order.
      n.children.forEach((c) => { c.depth = n.depth + 1; });
      sortRecursive(n.children);
    });
  };
  sortRecursive(roots);
  return roots;
}
